import FileActions from "components/FileDownloadButtons";
import React from "react";
import { useTranslation } from "contexts/i18nContext";

interface DetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: any;
}

const ReporterDetailModal: React.FC<DetailModalProps> = ({
  isOpen,
  onClose,
  data,
}) => {
  const { t } = useTranslation();

  if (!isOpen || !data) return null;

  const personalInfo = [
    { label: t("fullName"), value: data?.name, key: "name" },
    { label: t("phoneNumber"), value: data?.phone_number, key: "phone" },
    { label: t("emailAddress"), value: data?.email, key: "email" },
    { label: t("idNumber"), value: data?.id_numner, key: "id" },
  ];

  const locationInfo = [
    { label: t("completeAddress"), value: data?.address, key: "address" },
    { label: t("kebele"), value: data?.reporter_kebele, key: "kebele" },
    { label: t("woreda"), value: data?.reporter_woreda, key: "woreda" },
    { label: t("zone"), value: data?.reporter_zone, key: "zone" },
    { label: t("regionState"), value: data?.reporter_region, key: "region" },
    { label: t("cityTown"), value: data?.reporter_city, key: "city" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col mx-4 overflow-hidden border border-gray-200">
        <div className="bg-gradient-to-r from-red-900 via-red-800 to-red-900 text-white px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold tracking-tight">
                {t("reporterFile")}
              </h2>
              <p className="text-sm text-red-100 mt-1 opacity-90">
                {t("reporterInformationRecord")}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-red-200 hover:text-white text-2xl font-light transition-colors hover:bg-red-700 w-8 h-8 flex items-center justify-center rounded-full"
              aria-label={t("close")}
            >
              &times;
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4">
          <div className="space-y-8">
            <section>
              <div className="flex items-center mb-4">
                <div className="h-6 w-1 bg-red-700 rounded-r mr-3"></div>
                <h3 className="text-base font-semibold text-gray-800">
                  {t("reporterDetails")}
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-4">
                {personalInfo.map((item) => (
                  <div key={item.key} className="space-y-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {item.label}
                    </p>
                    <p className="text-sm font-normal text-gray-800 bg-gray-50 px-3 py-2 rounded border border-gray-200 min-h-[40px] flex items-center">
                      {item.value || (
                        <span className="text-gray-400 italic">
                          {t("notProvided")}
                        </span>
                      )}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <div className="flex items-center mb-4">
                <div className="h-6 w-1 bg-red-600 rounded-r mr-3"></div>
                <h3 className="text-base font-semibold text-gray-800">
                  {t("reporterLocation")}
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-4">
                {locationInfo.map((item) => (
                  <div key={item.key} className="space-y-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {item.label}
                    </p>
                    <p className="text-sm font-normal text-gray-800 bg-gray-50 px-3 py-2 rounded border border-gray-200 min-h-[40px] flex items-center">
                      {item.value || (
                        <span className="text-gray-400 italic">
                          {t("notProvided")}
                        </span>
                      )}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {data?.id_card_details && (
              <section>
                <div className="flex items-center mb-4">
                  <div className="h-6 w-1 bg-red-800 rounded-r mr-3"></div>
                  <h3 className="text-base font-semibold text-gray-800">
                    {t("identificationDocuments")}
                  </h3>
                </div>
                <div className="pl-4">
                  <div className="bg-red-50 rounded-lg border border-red-100 p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 flex items-center justify-center bg-red-100 border border-red-200 rounded-lg">
                            <svg
                              className="w-6 h-6 text-red-700"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800">
                            {data?.id_card_details?.file_name ||
                              t("reporterIdDocument")}
                          </p>
                          <div className="flex items-center space-x-3 mt-1">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                              {t("pdfDocument")}
                            </span>
                            {data?.id_card_details?.upload_date && (
                              <span className="text-xs text-gray-500">
                                {t("uploaded")}:{" "}
                                {data?.id_card_details?.upload_date}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <FileActions fileUrl={data?.id_card_details?.url || ""} />
                    </div>
                  </div>
                </div>
              </section>
            )}

            {data?.created_at && (
              <section className="pt-4 border-t border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("recorded")}
                    </p>
                    <p className="text-sm font-normal text-gray-600">
                      {data.created_at}
                    </p>
                  </div>
                  {data?.updated_at && data.updated_at !== data.created_at && (
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("lastUpdated")}
                      </p>
                      <p className="text-sm font-normal text-gray-600">
                        {data.updated_at}
                      </p>
                    </div>
                  )}
                </div>
              </section>
            )}
          </div>
        </div>

        <div className="bg-gradient-to-r from-red-50 via-white to-red-50 px-6 py-3 border-t border-red-100">
          <div className="flex justify-between items-center">
            <p className="text-xs text-gray-500 font-medium">
              {t("reporterRecord")} • {t("officialRecord")}
            </p>
            <button
              onClick={onClose}
              className="px-5 py-2 bg-red-700 text-white text-sm font-medium rounded-md hover:bg-red-800 transition-colors shadow-sm hover:shadow"
            >
              {t("closeFile")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReporterDetailModal;
